import NotificationPopover from '../common/NotificationPopover';
import '../styles/notificationDropdown.css';
import { getProfileRoute } from '../../utils/profileRoute';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import { Search, Bell, User, LogOut, LayoutGrid, Calendar, ChevronRight, X, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../services/authService.ts';
import Loading from '../Loading.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { useTheme } from '../../context/ThemeContext.tsx';
import { useWebSocketCitas } from '../../hooks/useWebSocket.ts';
import { fetchNotificaciones, updateNotificacion } from '../../services/notificacionService.ts';
import { TierAvatar, getUserRingTier, TIER_CONFIGS, getProfessionColor } from '../../utils/userTier.ts';
import DarkModeToggle from '../common/DarkModeToggle';

const colors = {
    primary: '#C1348B',
    primaryDark: '#61257B',
    secondary: '#61257B',
    secondaryLight: '#FFD97C',
    accentDark: '#EA8406',
};

interface Notificacion {
    id: number;
    citaId: number;
    codigo: string;
    servicio: string;
    fechaInicio: string;
    horaInicio: string;
    total: string | number;
    estado: string;
    leida: boolean;
    timestamp: Date;
}

const formatRelativeTime = (date: Date): string => {
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return 'Ahora mismo';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const Topbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);

    const navigate = useNavigate();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const notifRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();
    const { isDark } = useTheme();

    const noLeidas = notificaciones.filter(n => !n.leida).length;

    // ── Cargar notificaciones desde la API al montar el componente ──
    const loadNotificaciones = useCallback(async () => {
        if (!user) return;
        try {
            const data = await fetchNotificaciones();
            const notifs: Notificacion[] = data.map(notif => ({
                id: notif.id,
                citaId: notif.cita.id,
                codigo: notif.cita.codigo || `CITA-${notif.cita.id}`,
                servicio: notif.cita.servicio_info?.nombre || notif.cita.espacio_en_uso || 'Nueva reserva',
                fechaInicio: notif.cita.fecha_inicio || '',
                horaInicio: notif.cita.hora_inicio || '',
                total: notif.cita.total || 0,
                estado: notif.cita.estado || 'Confirmada',
                leida: notif.leida,
                timestamp: new Date(notif.fecha_creacion),
            }));
            setNotificaciones(notifs);
        } catch (error) {
            /* Browser diagnostics omitted. */
        }
    }, [user]);

    useEffect(() => {
        loadNotificaciones();
    }, [loadNotificaciones]);

    // ── WebSocket: capturar nuevas citas y recargar notificaciones ──
    const handleCitaEvent = useCallback(async (action: string, citaData: any) => {
        if (action === 'create' && citaData.cliente === user?.id) {
            // Recargar desde la API para obtener el ID real y evitar duplicados
            await loadNotificaciones();
        }
    }, [user?.id, loadNotificaciones]);

    useWebSocketCitas(handleCitaEvent);

    // ── Cerrar dropdowns al click fuera ──
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('[data-dropdown-trigger]') && !target.closest('[data-dropdown-content]')) {
                setMenuOpen(false);
                setNotifOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        setLoading(true);
        await logout();
        setLoading(false);
        navigate('/');
    };

    // Marcar una notificación como leída (API + estado local)
    const marcarLeida = async (id: number) => {
        try {
            await updateNotificacion(id, { leida: true });
            setNotificaciones(prev =>
                prev.map(n => (n.id === id ? { ...n, leida: true } : n))
            );
        } catch (error) {
            /* Browser diagnostics omitted. */
        }
    };

    // Marcar todas como leídas
    const marcarTodasLeidas = async () => {
        const noLeidasList = notificaciones.filter(n => !n.leida);
        for (const notif of noLeidasList) {
            try {
                await updateNotificacion(notif.id, { leida: true });
            } catch (error) {
                /* Browser diagnostics omitted. */
            }
        }
        setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
    };

    const eliminarNotif = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        // Opcional: también llamar a deleteNotificacion(id) si quieres borrar de la BD
        setNotificaciones(prev => prev.filter(n => n.id !== id));
    };

    const [tierTick, setTierTick] = useState(0);
    useEffect(() => {
        const onStorage = () => setTierTick(t => t + 1);
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const initials = user
        ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase()
        : 'U';
    const userTier = getUserRingTier(user);
    const tierConfig = TIER_CONFIGS[userTier] || TIER_CONFIGS.comun;
    const profColor = getProfessionColor(user);


    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
                .topbar-search::placeholder { color: #94a3b8; }
                .topbar-search:focus { outline: none; }
                .topbar-icon-btn { transition: all 0.2s cubic-bezier(.16,1,.3,1); }
                .topbar-icon-btn:hover { background: #f1f5f9 !important; transform: translateY(-1px); }
                .topbar-menu-item { transition: background 0.15s; }
                .topbar-menu-item:hover { background: #f8fafc !important; }
                .topbar-logout:hover { background: #fff5f6 !important; color:var(--ad-link, #2854c5) !important; }
                .topbar-logout:hover svg { color:var(--ad-link, #2854c5) !important; }
                .notif-item { transition: background 0.15s, transform 0.15s; cursor: pointer; }
                .notif-item:hover { background: #f8fafc !important; transform: translateX(2px); }
                .notif-scroll::-webkit-scrollbar { width: 4px; }
                .notif-scroll::-webkit-scrollbar-track { background: transparent; }
                .notif-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
                @keyframes pulseTopbar {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(193, 52, 139,0.4); }
                    50%      { box-shadow: 0 0 0 4px rgba(193, 52, 139,0); }
                }
                @keyframes notifSlideIn {
                    from { opacity: 0; transform: translateY(-6px) scale(0.98); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes badgePop {
                    0%   { transform: scale(0); }
                    70%  { transform: scale(1.2); }
                    100% { transform: scale(1); }
                }
                .notif-badge-anim { animation: badgePop 0.3s cubic-bezier(.16,1,.3,1) both; }
                .topbar-user-name { display: block; }
                .topbar-search-wrap { display: flex; }
                .topbar-dashboard-chip { display: inline-block; }
                @media (max-width: 640px) {
                    .topbar-user-name { display: none !important; }
                    .topbar-user-chevron { display: none !important; }
                    .topbar-search-wrap { display: none !important; }
                    .topbar-dashboard-chip { display: none !important; }
                }
                @media (max-width: 900px) {
                    .topbar-search-wrap { max-width: 180px !important; }
                }
            `}</style>

            <div style={{
                fontFamily: "'DM Sans', sans-serif",
                background: isDark ? '#0f081d' : '#fff',
                borderBottom: isDark ? '1px solid rgba(193, 52, 139, 0.22)' : '1px solid #f1f5f9',
                padding: '0 clamp(12px, 3vw, 24px)',
                height: '64px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : 'none',
                position: 'sticky', top: 0, zIndex: 50,
                transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}>

                {/* ── Izquierda ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px, 2vw, 24px)', flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <div style={{
                            width: '32px', height: '32px', borderRadius: '10px',
                            background: `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: `0 4px 12px ${colors.primary}30`,
                        }}>
                            <span style={{ fontSize: '0.7rem', fontWeight: '900', color: '#fff', letterSpacing: '-0.02em' }}>CM</span>
                        </div>
                        <span style={{ fontSize: '1.05rem', fontWeight: '800', color: isDark ? '#f8fafc' : '#0f172a', letterSpacing: '-0.02em' }}>
                            Co<span style={{ color:'var(--ad-link, #2854c5)' }}>Marca</span>
                        </span>
                        <span className="topbar-dashboard-chip" style={{
                            fontSize: '0.65rem', fontWeight: '700',
                            color: isDark ? '#cbd5e1' : '#94a3b8',
                            background: isDark ? '#1b1030' : '#f8fafc',
                            border: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #f1f5f9',
                            borderRadius: '6px', padding: '2px 7px', letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                        }}>
                            Dashboard
                        </span>
                    </div>

                    <div className="topbar-search-wrap" style={{ position: 'relative', flex: 1, maxWidth: '440px' }}>
                        <Search
                            size={15}
                            color={searchFocused ? colors.secondary : (isDark ? '#64748b' : '#94a3b8')}
                            style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', transition: 'color 0.2s', pointerEvents: 'none' }}
                        />
                        <input
                            type="text"
                            placeholder="Buscar reservas, espacios, servicios..."
                            className="topbar-search"
                            onFocus={() => setSearchFocused(true)}
                            onBlur={() => setSearchFocused(false)}
                            style={{
                                width: '100%',
                                padding: '9px 16px 9px 36px',
                                borderRadius: '12px',
                                border: searchFocused
                                    ? `1.5px solid ${colors.secondary}60`
                                    : (isDark ? '1.5px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9'),
                                background: searchFocused
                                    ? (isDark ? '#1a0d33' : '#fff')
                                    : (isDark ? '#16092b' : '#f8fafc'),
                                fontSize: '0.85rem',
                                color: isDark ? '#f8fafc' : '#374151',
                                fontFamily: "'DM Sans', sans-serif",
                                boxShadow: searchFocused ? `0 0 0 3px ${colors.secondary}15` : 'none',
                                transition: 'all 0.2s cubic-bezier(.16,1,.3,1)',
                            }}
                        />
                    </div>
                </div>

                {/* ── Derecha ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px, 1vw, 10px)', flexShrink: 0 }}>

                    {/* ── Switch Modo Noche / Día ── */}
                    <div style={{ display: 'flex', alignItems: 'center', marginRight: '4px' }} title="Alternar modo noche y día">
                        <DarkModeToggle fontSize="12px" />
                    </div>

                    {/* ── Campana de notificaciones ── */}
                    <div style={{ position: 'relative' }} ref={notifRef}>
                        <button
                            data-dropdown-trigger="notif"
                            aria-label="Notificaciones"
                            aria-expanded={notifOpen}
                            className="topbar-icon-btn"
                            onClick={() => { setNotifOpen(o => !o); setMenuOpen(false); }}
                            style={{
                                position: 'relative', width: '38px', height: '38px', borderRadius: '11px',
                                background: notifOpen
                                    ? (isDark ? '#26133f' : '#f1f5f9')
                                    : (isDark ? '#16092b' : '#f8fafc'),
                                border: notifOpen
                                    ? `1.5px solid ${colors.primary}50`
                                    : (isDark ? '1.5px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9'),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                            }}
                        >
                            <Bell size={16} color={notifOpen ? colors.primary : (isDark ? '#cbd5e1' : '#64748b')} />
                            {noLeidas > 0 && (
                                <span className="notif-badge-anim" style={{
                                    position: 'absolute', top: '5px', right: '5px',
                                    minWidth: '16px', height: '16px',
                                    borderRadius: '100px',
                                    background: colors.primary,
                                    border: isDark ? '2px solid #0f081d' : '2px solid #fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.55rem', fontWeight: '900', color: '#fff',
                                    padding: '0 3px',
                                }}>
                                    {noLeidas > 9 ? '9+' : noLeidas}
                                </span>
                            )}
                        </button>

                        {/* ── Dropdown notificaciones ── */}
                        <NotificationPopover open={notifOpen} anchorRef={notifRef} onClose={() => setNotifOpen(false)}>
                            <div
                                data-dropdown-content="notif"
                                className="notification-dropdown"
                                style={{
                                    position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                                    width: '360px',
                                    background: isDark ? '#150a24' : '#fff',
                                    borderRadius: '20px',
                                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                                    boxShadow: isDark
                                        ? '0 20px 60px rgba(0,0,0,0.85), 0 4px 20px rgba(0,0,0,0.5)'
                                        : '0 20px 60px rgba(0,0,0,0.12), 0 4px 20px rgba(0,0,0,0.06)',
                                    overflow: 'hidden', zIndex: 100,
                                    fontFamily: "'DM Sans', sans-serif",
                                    animation: 'notifSlideIn 0.2s cubic-bezier(.16,1,.3,1)',
                                }}
                            >
                                {/* Header */}
                                <div className="notification-dropdown-header" style={{
                                    padding: '16px 20px 14px',
                                    borderBottom: isDark ? '1.5px solid rgba(193, 52, 139, 0.2)' : '1.5px solid #f8fafc',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{
                                                width: '28px', height: '28px', borderRadius: '8px',
                                                background: `${colors.primary}12`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <Bell size={13} color={colors.primary} />
                                            </div>
                                            <span style={{ fontSize: '0.9rem', fontWeight: '800', color: isDark ? '#f8fafc' : '#0f172a' }}>
                                                Notificaciones
                                            </span>
                                            {noLeidas > 0 && (
                                                <span style={{
                                                    background: colors.primary, color: '#fff',
                                                    fontSize: '0.62rem', fontWeight: '800',
                                                    borderRadius: '100px', padding: '2px 7px',
                                                }}>
                                                    {noLeidas} nueva{noLeidas > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {noLeidas > 0 && (
                                        <button
                                            onClick={marcarTodasLeidas}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '4px',
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                fontSize: '0.72rem', fontWeight: '700',
                                                color:'var(--ad-link, #2854c5)', fontFamily: "'DM Sans', sans-serif",
                                                padding: '4px 8px', borderRadius: '8px',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = `${colors.secondary}10`; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                        >
                                            <CheckCheck size={12} />
                                            Marcar todas
                                        </button>
                                    )}
                                </div>

                                {/* Lista */}
                                <div className="notif-scroll" style={{ maxHeight: '380px', overflowY: 'auto' }}>
                                    {notificaciones.length === 0 ? (
                                        <div style={{
                                            display: 'flex', flexDirection: 'column',
                                            alignItems: 'center', padding: '48px 20px', gap: '12px',
                                        }}>
                                            <div style={{
                                                width: '52px', height: '52px', borderRadius: '16px',
                                                background: 'var(--ad-surface, #f8fafc)', border: '1.5px solid #f1f5f9',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <Bell size={22} color="#e2e8f0" />
                                            </div>
                                            <p style={{ fontSize: '0.85rem', fontWeight: '700', color: 'var(--ad-muted, #94a3b8)', textAlign: 'center' }}>
                                                Sin notificaciones
                                            </p>
                                            <p style={{ fontSize: '0.75rem', color: '#cbd5e1', fontWeight: '500', textAlign: 'center' }}>
                                                Las nuevas reservas aparecerán aquí
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '8px' }}>
                                            {notificaciones.map(notif => (
                                                <div
                                                    key={notif.id}
                                                    className="notif-item"
                                                    onClick={() => {
                                                        marcarLeida(notif.id);
                                                        navigate(`/mis-citas/${notif.citaId}`);
                                                        setNotifOpen(false);
                                                    }}
                                                    style={{
                                                        borderRadius: '14px',
                                                        padding: '12px 14px',
                                                        marginBottom: '4px',
                                                        background: notif.leida ? '#fff' : `${colors.primary}05`,
                                                        border: notif.leida
                                                            ? '1.5px solid transparent'
                                                            : `1.5px solid ${colors.primary}18`,
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {/* Dot no leída */}
                                                    {!notif.leida && (
                                                        <span style={{
                                                            position: 'absolute', top: '14px', right: '36px',
                                                            width: '7px', height: '7px', borderRadius: '50%',
                                                            background: colors.primary,
                                                        }} />
                                                    )}

                                                    {/* Botón eliminar */}
                                                    <button
                                                        onClick={(e) => eliminarNotif(notif.id, e)}
                                                        style={{
                                                            position: 'absolute', top: '10px', right: '10px',
                                                            width: '20px', height: '20px', borderRadius: '6px',
                                                            background: 'none', border: 'none',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            cursor: 'pointer', color: '#cbd5e1',
                                                            transition: 'color 0.15s, background 0.15s',
                                                        }}
                                                        onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#94a3b8'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#cbd5e1'; }}
                                                    >
                                                        <X size={11} />
                                                    </button>

                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', paddingRight: '24px' }}>
                                                        {/* Icono */}
                                                        <div style={{
                                                            width: '36px', height: '36px', borderRadius: '11px', flexShrink: 0,
                                                            background: notif.leida
                                                                ? '#f8fafc'
                                                                : `linear-gradient(135deg, ${colors.primaryDark}, ${colors.primary})`,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            <Calendar
                                                                size={16}
                                                                color={notif.leida ? '#94a3b8' : '#fff'}
                                                            />
                                                        </div>

                                                        {/* Contenido */}
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                                                                <span style={{
                                                                    fontSize: '0.78rem', fontWeight: '800',
                                                                    color: notif.leida ? '#374151' : '#0f172a',
                                                                }}>
                                                                    Nueva reserva confirmada
                                                                </span>
                                                            </div>

                                                            <p style={{
                                                                fontSize: '0.75rem', color: 'var(--ad-muted, #64748b)', fontWeight: '600',
                                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                marginBottom: '6px',
                                                            }}>
                                                                {notif.servicio}
                                                            </p>

                                                            {/* Chips de detalle */}
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '6px' }}>
                                                                <span style={{
                                                                    fontSize: '0.65rem', fontWeight: '700',
                                                                    background: 'var(--ad-surface, #f1f5f9)', color: 'var(--ad-muted, #64748b)',
                                                                    borderRadius: '6px', padding: '2px 7px',
                                                                    fontFamily: 'monospace',
                                                                }}>
                                                                    {notif.codigo}
                                                                </span>
                                                                {notif.fechaInicio && (
                                                                    <span style={{
                                                                        fontSize: '0.65rem', fontWeight: '700',
                                                                        background: `${colors.secondary}10`,
                                                                        color:'var(--ad-link, #2854c5)',
                                                                        border: `1px solid ${colors.secondary}20`,
                                                                        borderRadius: '6px', padding: '2px 7px',
                                                                    }}>
                                                                        {new Date(notif.fechaInicio + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                        {notif.horaInicio && ` · ${notif.horaInicio.slice(0, 5)}`}
                                                                    </span>
                                                                )}
                                                                <span style={{
                                                                    fontSize: '0.65rem', fontWeight: '700',
                                                                    background: `${colors.accentDark}10`,
                                                                    color: colors.accentDark,
                                                                    border: `1px solid ${colors.accentDark}20`,
                                                                    borderRadius: '6px', padding: '2px 7px',
                                                                }}>
                                                                    Bs {Number(notif.total).toFixed(2)}
                                                                </span>
                                                            </div>

                                                            {/* Footer: tiempo + CTA */}
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <span style={{ fontSize: '0.68rem', color: 'var(--ad-muted, #94a3b8)', fontWeight: '500' }}>
                                                                    {formatRelativeTime(notif.timestamp)}
                                                                </span>
                                                                <span style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                                    fontSize: '0.68rem', fontWeight: '700', color:'var(--ad-link, #2854c5)',
                                                                }}>
                                                                    Ver detalles <ChevronRight size={10} />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                {notificaciones.length > 0 && (
                                    <div style={{
                                        padding: '10px 16px',
                                        borderTop: '1.5px solid #f8fafc',
                                        display: 'flex', justifyContent: 'center',
                                    }}>
                                        <button
                                            onClick={() => { navigate('/mis-citas'); setNotifOpen(false); }}
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                fontSize: '0.78rem', fontWeight: '700',
                                                color:'var(--ad-link, #2854c5)', fontFamily: "'DM Sans', sans-serif",
                                                display: 'flex', alignItems: 'center', gap: '4px',
                                                padding: '6px 12px', borderRadius: '10px',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = `${colors.secondary}10`; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                                        >
                                            Ver todas mis reservas <ChevronRight size={12} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </NotificationPopover>
                    </div>

                    <div style={{ width: '1px', height: '24px', background: 'var(--ad-surface, #f1f5f9)', margin: '0 4px' }} />

                    {/* ── Menú usuario (adaptado modo noche) ── */}
                    <div style={{ position: 'relative' }} ref={dropdownRef}>
                        <button
                            data-dropdown-trigger="user-menu"
                            onClick={() => { setMenuOpen(!menuOpen); setNotifOpen(false); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '5px',
                                borderRadius: '12px',
                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9',
                                background: menuOpen
                                    ? (isDark ? '#231238' : '#f8fafc')
                                    : (isDark ? '#150a24' : '#fff'),
                                cursor: 'pointer', transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = isDark ? '#231238' : '#f8fafc'; }}
                            onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = isDark ? '#150a24' : '#fff'; }}
                        >
                            <TierAvatar
                                user={user}
                                tier={userTier}
                                size={32}
                                ringThickness={2.5}
                                initial={initials}
                                bgColor={profColor.color}
                                fotoUrl={user?.foto_url}
                                isOpenOrHovered={menuOpen}
                            />
                            <div className="topbar-user-name" style={{ textAlign: 'left', minWidth: '80px' }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: '700', color: isDark ? '#f8fafc' : '#0f172a', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                    {user ? `${user.first_name} ${user.last_name}` : 'Usuario'}
                                </div>
                                <div style={{ fontSize: '0.65rem', color: tierConfig.badgeColor, fontWeight: '700', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                    <span>{tierConfig.iconText}</span> {tierConfig.label}
                                </div>
                            </div>
                            <svg className="topbar-user-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none"
                                style={{ color: isDark ? '#cbd5e1' : '#94a3b8', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <Transition
                            show={menuOpen}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                        >
                            <div data-dropdown-content="user-menu" style={{
                                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                                width: '230px',
                                background: isDark ? '#150a24' : '#fff',
                                borderRadius: '16px',
                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.28)' : '1.5px solid #f1f5f9',
                                boxShadow: isDark
                                    ? '0 16px 48px rgba(0,0,0,0.85), 0 4px 16px rgba(0,0,0,0.4)'
                                    : '0 16px 48px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.06)',
                                overflow: 'hidden', zIndex: 100,
                                fontFamily: "'DM Sans', sans-serif",
                            }}>
                                <div style={{
                                    padding: '16px',
                                    background: `linear-gradient(135deg, ${profColor.color}15, ${profColor.colorDark}15)`,
                                    borderBottom: isDark ? '1px solid rgba(193, 52, 139, 0.2)' : '1px solid #f1f5f9',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <TierAvatar
                                            user={user}
                                            tier={userTier}
                                            size={42}
                                            ringThickness={3}
                                            initial={initials}
                                            bgColor={profColor.color}
                                            fotoUrl={user?.foto_url}
                                            isOpenOrHovered={true}
                                        />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontSize: '0.88rem', fontWeight: '800', color: isDark ? '#f8fafc' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {user ? `${user.first_name} ${user.last_name}` : 'Usuario'}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                    background: tierConfig.badgeBg, border: tierConfig.badgeBorder,
                                                    borderRadius: '100px', padding: '1px 7px',
                                                    fontSize: '0.62rem', fontWeight: '800', color: tierConfig.badgeColor,
                                                }}>
                                                    <span>{tierConfig.iconText}</span> {tierConfig.label}
                                                </div>
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center',
                                                    background: `${profColor.color}18`, borderRadius: '100px', padding: '1px 6px',
                                                    fontSize: '0.60rem', fontWeight: '700', color: profColor.color,
                                                }}>
                                                    {profColor.label}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ padding: '8px' }}>
                                    <a href={getProfileRoute(user)} className="topbar-menu-item" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '10px', fontSize: '0.83rem', fontWeight: '600', color: isDark ? '#e2e8f0' : '#374151', textDecoration: 'none' }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `${colors.secondary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <User size={13} color={colors.secondary} />
                                        </div>
                                        Mi Perfil
                                    </a>
                                    <a href="/" className="topbar-menu-item" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '10px', fontSize: '0.83rem', fontWeight: '600', color: isDark ? '#e2e8f0' : '#374151', textDecoration: 'none' }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `${colors.secondaryLight}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <LayoutGrid size={13} color={colors.secondaryLight} />
                                        </div>
                                        Volver al Inicio
                                    </a>
                                    <div style={{ height: '1px', background: isDark ? 'rgba(193, 52, 139, 0.2)' : '#f1f5f9', margin: '6px 4px' }} />
                                    <button onClick={handleLogout} className="topbar-logout" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '10px', fontSize: '0.83rem', fontWeight: '600', color: isDark ? '#f87171' : '#374151', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", textAlign: 'left', transition: 'all 0.15s' }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `${colors.primary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <LogOut size={13} color={colors.primary} />
                                        </div>
                                        Cerrar Sesión
                                    </button>
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>
            </div>

            {loading && <Loading text="Cerrando sesión..." fullScreen={true} />}
        </>
    );
};

export default Topbar;

